import React from 'react';
import {
  Tooltip,
  Stack,
  Box,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Fab,
  CircularProgress
} from '@mui/material';
import SaveIcon from '@mui/icons-material/SaveAsOutlined';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import { green } from '@mui/material/colors';

const EditableTextField = ({
  title,
  value,
  loading,
  success,
  error,
  disabled,
  readOnly,
  onChange,
  onSave,
  onCancel,
  onKeyDown,
  isRequired,
  hasChanges
}) => {
  const buttonSx = {
    ...(success && {
      bgcolor: green[500],
      '&:hover': {
        bgcolor: green[700],
      },
    }),
  };

  const currentValue = value || '';
  const isEmpty = currentValue.trim() === '';

  const showSuccessIcon = success && !hasChanges;
  const showErrorIcon = error;

  return (
    <Tooltip title={isEmpty && isRequired ? "Value cannot be empty" : "Press Enter to Save, Esc to Cancel"}>
      <FormControl sx={{ m: 0.5, width: 'auto' }} variant="outlined">
        <InputLabel sx={{ fontWeight: 'normal' }}>{title}</InputLabel>
        <Stack direction="row" spacing={1} alignItems="center">
          <OutlinedInput
            size="small"
            label={title}
            color="secondary"
            focused
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            disabled={disabled}
            input={{ readOnly }}
            sx={{
              fontSize: '12px',
              height: 36,
            }}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="save"
                  edge="end"
                  disabled={loading || isEmpty || !hasChanges}
                  onClick={async () => {
                    if (!isEmpty && hasChanges) await onSave();
                  }}
                >
                  <Box sx={{ m: 0.5, position: 'relative' }}>
                    <Fab
                      aria-label="save"
                      size="small"
                      color={error ? 'error' : 'primary'}
                      sx={{
                        ...buttonSx,
                        width: 18,
                        height: 18,
                        minHeight: 'unset',
                      }}
                      disabled={loading || isEmpty || !hasChanges}
                    >
                      {showErrorIcon ? (
                        <ErrorIcon sx={{ fontSize: 14 }} />
                      ) : showSuccessIcon ? (
                        <CheckIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <SaveIcon sx={{ fontSize: 14 }} />
                      )}
                    </Fab>
                    {loading && (
                      <CircularProgress
                        size={24}
                        sx={{
                          color: green[500],
                          position: 'absolute',
                          top: 3,
                          left: -3,
                          zIndex: 1,
                        }}
                      />
                    )}
                  </Box>
                </IconButton>
              </InputAdornment>
            }
          />
          <Box sx={{ m: 0.5, position: 'relative' }}>
            <IconButton
              aria-label="cancel"
              color="error"
              size="small"
              sx={{
                width: 18,
                height: 18,
                minHeight: 'unset',
              }}
              onClick={onCancel}
              disabled={loading}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Stack>
      </FormControl>
    </Tooltip>
  );
};

export default EditableTextField;
